// ATE-CR-117
import { useId, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useTenantContext } from "@/context/TenantContext";
import { useMe } from "@/hooks/useMe";

import { BalanceTab } from "./BalanceTab";
import { HoldsTab } from "./HoldsTab";
import { RefundsTab } from "./RefundsTab";
import { TransactionsTab } from "./TransactionsTab";
import "./CreditsPage.css";

type TabKey = "balance" | "holds" | "transactions" | "refunds";

function hashToTab(hash: string): TabKey {
  const stripped = hash.replace(/^#/, "");
  if (
    stripped === "holds" ||
    stripped === "transactions" ||
    stripped === "refunds"
  ) {
    return stripped;
  }
  return "balance";
}

// ATE-CR-117
export function CreditsPage(): JSX.Element {
  const { scope } = useTenantContext();
  const { me } = useMe();
  const location = useLocation();
  const navigate = useNavigate();

  const baseId = useId();
  const tabId = (key: TabKey) => `${baseId}-tab-${key}`;
  const panelId = (key: TabKey) => `${baseId}-panel-${key}`;

  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Delta 19: HQ-scope placeholder — spec doesn't explicitly define this behaviour.
  // We show a friendly placeholder rather than firing API calls with no tenant.
  if (scope.mode === "hq") {
    return (
      <div className="credits-page credits-page--placeholder">
        <p>Select a tenant from the sidebar to view credits.</p>
      </div>
    );
  }

  const tenantId = scope.tenant.id;
  const activeTab = hashToTab(location.hash);
  // Delta 18: credits:add gated on SUPER_ADMIN because MeResponse has no permissions[] field.
  // When backend ships a proper permissions[] field this can be tightened in a follow-up CR.
  const canAddCredits = me?.system_role === "SUPER_ADMIN";

  const TABS: { key: TabKey; label: string; gated?: boolean }[] = [
    { key: "balance", label: "Balance" },
    { key: "holds", label: "Holds" },
    { key: "transactions", label: "Transactions" },
    { key: "refunds", label: "Refunds", gated: true },
  ];

  function handleTabClick(key: TabKey): void {
    navigate(`#${key}`, { replace: true });
  }

  function handleTabKeyDown(e: React.KeyboardEvent<HTMLDivElement>): void {
    const tabKeys = TABS.filter((t) => !t.gated || canAddCredits).map((t) => t.key);
    const currentIdx = tabKeys.indexOf(activeTab);
    let nextIdx: number | null = null;
    if (e.key === "ArrowRight") nextIdx = (currentIdx + 1) % tabKeys.length;
    else if (e.key === "ArrowLeft") nextIdx = (currentIdx - 1 + tabKeys.length) % tabKeys.length;
    else if (e.key === "Home") nextIdx = 0;
    else if (e.key === "End") nextIdx = tabKeys.length - 1;
    if (nextIdx === null) return;
    e.preventDefault();
    const nextKey = tabKeys[nextIdx];
    handleTabClick(nextKey);
    tabRefs.current[nextKey]?.focus();
  }

  function renderPanel(): JSX.Element {
    switch (activeTab) {
      case "holds":
        return <HoldsTab tenantId={tenantId} />;
      case "transactions":
        return <TransactionsTab tenantId={tenantId} />;
      case "refunds":
        if (!canAddCredits) return <></>;
        return <RefundsTab tenantId={tenantId} />;
      default:
        return <BalanceTab tenantId={tenantId} />;
    }
  }

  return (
    <div className="credits-page">
      <header className="credits-page__header">
        <h1 className="credits-page__title">Credits</h1>
        <span className="credits-page__tenant">{scope.tenant.name}</span>
      </header>

      <nav aria-label="Credits tabs">
        <div role="tablist" className="credits-page__tablist" onKeyDown={handleTabKeyDown}>
          {TABS.filter((t) => !t.gated || canAddCredits).map(({ key, label }) => (
            <button
              key={key}
              type="button"
              role="tab"
              id={tabId(key)}
              aria-selected={activeTab === key}
              aria-controls={panelId(key)}
              tabIndex={key === activeTab ? 0 : -1}
              className={`credits-page__tab${activeTab === key ? " credits-page__tab--active" : ""}`}
              onClick={() => handleTabClick(key)}
              ref={(el) => { tabRefs.current[key] = el; }}
            >
              {label}
            </button>
          ))}
        </div>
      </nav>

      <div
        role="tabpanel"
        id={panelId(activeTab)}
        aria-labelledby={tabId(activeTab)}
        className="credits-page__panel"
      >
        {renderPanel()}
      </div>
    </div>
  );
}
